import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Stack,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  History as HistoryIcon,
  Devices as DevicesIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Logout as LogoutIcon,
  Visibility as VisibilityIcon,
  PhoneIphone as PhoneIphoneIcon,
  Android as AndroidIcon,
  PhoneAndroid as PhoneAndroidIcon,
  TabletMac as TabletMacIcon,
  Tablet as TabletIcon,
  Computer as ComputerIcon,
  LaptopMac as LaptopMacIcon,
  Terminal as TerminalIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  Place as PlaceIcon,
  AccessTime as AccessTimeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ExpandLess as ExpandLessIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { API_CONFIG } from '../config/config';
import MFASetup from '../components/MFASetup';

const passwordValidationSchema = yup.object({
  current_password: yup
    .string()
    .required('Current password is required'),
  new_password: yup
    .string()
    .min(12, 'Password should be of minimum 12 characters length')
    .max(128, 'Password should not exceed 128 characters')
    .matches(/^[\s\S]*$/, 'Password can contain any characters')
    .required('New password is required'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password')], 'Passwords must match')
    .required('Please confirm your new password'),
});

// Create validation schema function that can access user context
const createProfileValidationSchema = (user) => yup.object({
  first_name: yup
    .string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must not exceed 50 characters')
    .required('First name is required'),
  last_name: yup
    .string()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must not exceed 50 characters')
    .required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  password: yup
    .string()
    .when('email', {
      is: (email) => email && email !== user?.email,
      then: (schema) => schema.required('Password is required to change email address'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const Profile = () => {
  const navigate = useNavigate();
  const { user, getToken, updateUser, logout, forceRevalidate } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showMFADisable, setShowMFADisable] = useState(false);
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');

  // New state for enhanced features
  const [editingProfile, setEditingProfile] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showLogoutAllDialog, setShowLogoutAllDialog] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionSecurity, setSessionSecurity] = useState(null);
  const [otherSessionsCount, setOtherSessionsCount] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [loginHistorySummary, setLoginHistorySummary] = useState(null);
  const [showAllLoginHistory, setShowAllLoginHistory] = useState(false);
  const [loginHistoryFilter, setLoginHistoryFilter] = useState('all'); // all, recent, mobile, desktop
  const [loginHistorySearch, setLoginHistorySearch] = useState('');
  const [loginHistoryPage, setLoginHistoryPage] = useState(0);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const [userQuestions, setUserQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [preferences, setPreferences] = useState({
    language: 'de',
    theme: 'light',
    timezone: 'Europe/Berlin',
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
    profile: false,
  });

  // Password change form
  const passwordFormik = useFormik({
    initialValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values) => {
      setError('');
      setSuccess('');
      setLoading(true);

      try {
        const token = getToken();
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/change-password`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Passwortänderung fehlgeschlagen');
        }

        setSuccess('Passwort erfolgreich geändert');
        passwordFormik.resetForm();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
  });

  // Profile update form
  const profileFormik = useFormik({
    initialValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      password: '',
    },
    validationSchema: createProfileValidationSchema(user),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError('');
      setSuccess('');
      setLoading(true);

      try {
        const token = getToken();
        
        // Prepare data to send - only include password if email is being changed
        const dataToSend = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
        };
        
        // Check if email is being changed
        if (values.email && values.email !== user?.email) {
          // Use the secure email change endpoint
          const emailChangeData = {
            new_email: values.email,
            password: values.password,
          };
          
          const response = await fetch(`${API_CONFIG.BASE_URL}/auth/change-email`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(emailChangeData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'E-Mail-Änderung fehlgeschlagen');
          }

          const responseData = await response.json();
          setSuccess(responseData.message);
          setEditingProfile(false);
          profileFormik.setFieldValue('password', '');
          return; // Exit early for email changes
        }
        
        // For non-email changes, use the regular profile update
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/profile`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Profilaktualisierung fehlgeschlagen');
        }

        const responseData = await response.json();
        setSuccess('Profil erfolgreich aktualisiert');
        setEditingProfile(false);
        // Clear password field
        profileFormik.setFieldValue('password', '');
        // Update user data with the response from server
        updateUser(responseData.user);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
  });

  // Handle canceling profile editing
  const handleCancelProfileEdit = () => {
    setEditingProfile(false);
    profileFormik.resetForm();
    profileFormik.setFieldValue('password', '');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
    // Fetch questions when switching to question history tab
    if (newValue === 4) {
      fetchUserQuestions();
    }
  };

  const handleMFAEnable = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to setup MFA');
      }

      const data = await response.json();
      setSecret(data.secret);
      setQrCode(data.qr_code);
      setShowMFASetup(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMFADisable = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/mfa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: mfaDisableCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to disable MFA');
      }

      setSuccess('MFA erfolgreich deaktiviert');
      setShowMFADisable(false);
      setMfaDisableCode('');
      
      // Update user data with MFA disabled
      updateUser({
        ...user,
        mfa_enabled: false
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMFASetupComplete = (codes) => {
    setBackupCodes(codes);
    setShowBackupCodes(true);
    setShowMFASetup(false);
    setSuccess('MFA erfolgreich aktiviert');
    
    // Update user data with MFA enabled
    updateUser({
      ...user,
      mfa_enabled: true
    });
  };

  const handleMFASetupCancel = () => {
    setShowMFASetup(false);
  };

  const handleShowBackupCodes = async () => {
    setError('');
    setLoading(true);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/mfa/backup-codes`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch backup codes');
      }

      const data = await response.json();
      setBackupCodes(data.backup_codes || []);
      setShowBackupCodes(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBackupCodes = () => {
    setShowBackupCodes(false);
  };

  // New handler functions
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'LÖSCHEN') {
      setError('Bitte gib "LÖSCHEN" ein, um dein Konto zu löschen');
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Kontolöschung fehlgeschlagen');
      }

      // Logout and redirect
      logout();
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/export-data`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Datenexport fehlgeschlagen');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cyberlagebild-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess('Daten erfolgreich exportiert');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAllSessions = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/logout-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Abmeldung aller Sitzungen fehlgeschlagen' }));
        throw new Error(errorData.detail || 'Abmeldung aller Sitzungen fehlgeschlagen');
      }

      const data = await response.json();
      setSuccess(data.message || 'Alle anderen Sitzungen wurden beendet');
      setShowLogoutAllDialog(false);
      
      // Update the stored token if a new one was provided
      if (data.new_token) {
        localStorage.setItem('token', data.new_token);
        // Force revalidation of the new token
        if (forceRevalidate) {
          await forceRevalidate();
        }
      }
      
      // Reload sessions to show updated list
      await loadAdditionalData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load user questions
  const fetchUserQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_CONFIG.BASE_URL}/community/questions/my-questions?limit=100`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserQuestions(data.questions || []);
      }
    } catch (err) {
      console.error('Error fetching user questions:', err);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Toggle question expansion
  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Format date helper
  const formatQuestionDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper functions for login history
  const loadLoginHistory = async (page = 0, search = '', deviceFilter = '', append = false) => {
    try {
      const token = getToken();
      if (!token) return;

      const params = new URLSearchParams({
        limit: '5',
        offset: (page * 5).toString(),
        include_summary: 'true'
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      if (deviceFilter && deviceFilter !== 'all') {
        params.append('device_filter', deviceFilter);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/login-history?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const processedHistory = (data.login_history || []).map(login => ({
          ...login,
          timestamp: login.timestamp ? new Date(login.timestamp) : new Date()
        }));
        
        if (append && page > 0) {
          // Append to existing list for pagination
          setLoginHistory(prev => [...prev, ...processedHistory]);
        } else {
          // Replace list for new search/filter
          setLoginHistory(processedHistory);
        }
        
        setLoginHistorySummary(data.summary || null);
        setLoginHistoryTotal(data.total || processedHistory.length);
        setLoginHistoryPage(page);
      }
    } catch (error) {
      console.error('Error loading login history:', error);
    }
  };

  const handleLoginHistorySearch = (searchValue) => {
    setLoginHistorySearch(searchValue);
    setLoginHistoryPage(0);
    loadLoginHistory(0, searchValue, loginHistoryFilter);
  };

  const handleLoginHistoryFilter = (filterValue) => {
    setLoginHistoryFilter(filterValue);
    setLoginHistoryPage(0);
    loadLoginHistory(0, loginHistorySearch, filterValue);
  };

  const loadMoreLoginHistory = () => {
    const nextPage = loginHistoryPage + 1;
    loadLoginHistory(nextPage, loginHistorySearch, loginHistoryFilter, true);
  };

  const loadMoreSessions = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const nextPage = sessionsPage + 1;
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/sessions?limit=5&offset=${nextPage * 5}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const processedSessions = (data.sessions || []).map(session => ({
          ...session,
          lastActivity: session.lastActivity ? new Date(session.lastActivity) : new Date()
        }));
        setActiveSessions(prev => [...prev, ...processedSessions]);
        setSessionsHasMore(data.has_more || false);
        setSessionsPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more sessions:', error);
    }
  };




  // Remove the old filterLoginHistory function as filtering is now done server-side

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Gerade eben';
    if (diffInMinutes < 60) return `vor ${diffInMinutes} Min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `vor ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `vor ${diffInDays} Tag${diffInDays > 1 ? 'en' : ''}`;
    
    return date.toLocaleDateString('de-DE');
  };

  const handleTerminateSuspiciousSessions = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/terminate-suspicious-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Beenden verdächtiger Sitzungen fehlgeschlagen' }));
        throw new Error(errorData.detail || 'Beenden verdächtiger Sitzungen fehlgeschlagen');
      }

      const data = await response.json();
      
      // Check if current session was terminated
      if (data.status === 'logout_required' && data.current_session_terminated) {
        setSuccess(data.message || 'Ihre Sitzung wurde beendet. Sie werden abgemeldet.');
        
        // Force logout after a short delay to show the message
        setTimeout(async () => {
          await logout(true); // Redirect to login
        }, 2000);
      } else {
        setSuccess(data.message || 'Verdächtige Sitzungen wurden beendet');
        
        // Reload session security data
        await loadAdditionalData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Helper function to format dates safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unbekannt';
      return date.toLocaleDateString('de-DE');
    } catch (error) {
      return 'Unbekannt';
    }
  };

  const getDeviceIcon = (iconName) => {
    const iconMap = {
      'PhoneIphoneIcon': PhoneIphoneIcon,
      'AndroidIcon': AndroidIcon,
      'PhoneAndroidIcon': PhoneAndroidIcon,
      'TabletMacIcon': TabletMacIcon,
      'TabletIcon': TabletIcon,
      'ComputerIcon': ComputerIcon,
      'LaptopMacIcon': LaptopMacIcon,
      'TerminalIcon': TerminalIcon,
    };
    return iconMap[iconName] || ComputerIcon;
  };

  // Load additional data function (sessions and login history)
  const loadAdditionalData = async () => {
    try {
      const token = getToken();
      if (!token) return;

      // Load login history from backend with summary
      const loginHistoryResponse = await fetch(`${API_CONFIG.BASE_URL}/auth/login-history?limit=5&offset=0&include_summary=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (loginHistoryResponse.ok) {
        const loginData = await loginHistoryResponse.json();
        // Convert ISO strings to Date objects
        const processedLoginHistory = (loginData.login_history || []).map(login => ({
          ...login,
          timestamp: login.timestamp ? new Date(login.timestamp) : new Date()
        }));
        setLoginHistory(processedLoginHistory);
        setLoginHistorySummary(loginData.summary || null);
        setLoginHistoryTotal(loginData.total || processedLoginHistory.length);
      }

      // Load active sessions from backend
      const sessionsResponse = await fetch(`${API_CONFIG.BASE_URL}/auth/sessions?limit=5&offset=0`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          // Convert ISO strings to Date objects
          const processedSessions = (sessionsData.sessions || []).map(session => ({
            ...session,
            lastActivity: session.lastActivity ? new Date(session.lastActivity) : new Date()
          }));
          setActiveSessions(processedSessions);
          setOtherSessionsCount(sessionsData.other_sessions_count || 0);
          setSessionsTotal(sessionsData.total_sessions || 0);
          setSessionsHasMore(sessionsData.has_more || false);
          setSessionsPage(0);
        }

        // Load session security information
        const securityResponse = await fetch(`${API_CONFIG.BASE_URL}/auth/session-security`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();
          setSessionSecurity(securityData);
        }

        // Load suspicious sessions
    } catch (err) {
      console.error('Error loading additional data:', err);
    }
  };

  // Load user profile data and additional data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const token = getToken();
        if (!token) return;

        // Load complete user profile data
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const profileData = await response.json();
          // Update user context with complete profile data
          updateUser(profileData);
        }
      } catch (err) {
        console.error('Error loading user profile:', err);
      }
    };

    loadUserProfile();
    loadAdditionalData();
  }, [getToken, updateUser]);

  return (
    <Box sx={{ 
      maxWidth: 1200, 
      mx: 'auto', 
      mt: { xs: 2, sm: 4 }, 
      mb: { xs: 2, sm: 4 }, 
      px: { xs: 1, sm: 2 } 
    }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Zurück zum Dashboard
      </Button>

      {/* Header with Profile Info */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
              }
            >
              <Avatar
                sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
            </Badge>
            <Box sx={{ flex: 1 }}>
        <Typography variant="h4" gutterBottom>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {user?.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="Konto aktiv" 
                  color="success" 
                  size="small" 
                />
                {user?.mfa_enabled && (
                  <Chip 
                    icon={<SecurityIcon />} 
                    label="2FA aktiviert" 
                    color="primary" 
                    size="small" 
                  />
                )}
                <Chip 
                  label={`Mitglied seit ${formatDate(user?.created_at)}`}
                  variant="outlined" 
                  size="small" 
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Main Settings */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
          Profileinstellungen
        </Typography>

        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            mb: 3,
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: { xs: 1, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        >
              <Tab 
                icon={<PersonIcon />} 
                label="Persönliche Daten"
                sx={{ display: { xs: 'flex', sm: 'flex' } }}
              />
              <Tab 
                icon={<SecurityIcon />} 
                label="Sicherheit"
                sx={{ display: { xs: 'flex', sm: 'flex' } }}
              />
              <Tab 
                icon={<DevicesIcon />} 
                label="Geräte & Sitzungen"
                sx={{ display: { xs: 'flex', sm: 'flex' } }}
              />
              <Tab 
                icon={<HistoryIcon />} 
                label="Anmeldeverlauf"
                sx={{ display: { xs: 'flex', sm: 'flex' } }}
              />
              <Tab 
                icon={<QuestionAnswerIcon />} 
                label="Fragenverlauf"
                sx={{ display: { xs: 'flex', sm: 'flex' } }}
              />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

            {/* Personal Information Tab */}
        {activeTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Persönliche Informationen</Typography>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => setEditingProfile(!editingProfile)}
                    variant={editingProfile ? "outlined" : "contained"}
                  >
                    {editingProfile ? 'Abbrechen' : 'Bearbeiten'}
                  </Button>
                </Box>

                {editingProfile ? (
                  <form onSubmit={profileFormik.handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          id="first_name"
                          name="first_name"
                          label="Vorname"
                          value={profileFormik.values.first_name}
                          onChange={profileFormik.handleChange}
                          onBlur={profileFormik.handleBlur}
                          error={profileFormik.touched.first_name && Boolean(profileFormik.errors.first_name)}
                          helperText={profileFormik.touched.first_name && profileFormik.errors.first_name}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          id="last_name"
                          name="last_name"
                          label="Nachname"
                          value={profileFormik.values.last_name}
                          onChange={profileFormik.handleChange}
                          onBlur={profileFormik.handleBlur}
                          error={profileFormik.touched.last_name && Boolean(profileFormik.errors.last_name)}
                          helperText={profileFormik.touched.last_name && profileFormik.errors.last_name}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          label="E-Mail-Adresse"
                          type="email"
                          value={profileFormik.values.email}
                          onChange={profileFormik.handleChange}
                          onBlur={profileFormik.handleBlur}
                          error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
                          helperText={profileFormik.touched.email && profileFormik.errors.email}
                        />
                      </Grid>
                      {/* Show password field only when email is being changed */}
                      {profileFormik.values.email && profileFormik.values.email !== user?.email && (
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              <strong>Sicherheitshinweis:</strong> Um deine E-Mail-Adresse zu ändern, musst du dein aktuelles Passwort eingeben.
                            </Typography>
                          </Alert>
                          <TextField
                            fullWidth
                            id="password"
                            name="password"
                            label="Aktuelles Passwort (für E-Mail-Änderung erforderlich)"
                            type={showPassword.profile ? 'text' : 'password'}
                            value={profileFormik.values.password}
                            onChange={profileFormik.handleChange}
                            onBlur={profileFormik.handleBlur}
                            error={profileFormik.touched.password && Boolean(profileFormik.errors.password)}
                            helperText={profileFormik.touched.password && profileFormik.errors.password}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  onClick={() => togglePasswordVisibility('profile')}
                                  edge="end"
                                >
                                  {showPassword.profile ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              ),
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Speichern'}
                      </Button>
                      <Button
                        onClick={handleCancelProfileEdit}
                        variant="outlined"
                      >
                        Abbrechen
                      </Button>
                    </Box>
                  </form>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vorname"
                        value={user?.first_name || ''}
                        disabled
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nachname"
                        value={user?.last_name || ''}
                        disabled
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="E-Mail-Adresse"
                        value={user?.email || ''}
                        disabled
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {/* Security Tab */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>Passwort ändern</Typography>
                <form onSubmit={passwordFormik.handleSubmit}>
            <TextField
              fullWidth
              id="current_password"
              name="current_password"
              label="Aktuelles Passwort"
                    type={showPassword.current ? 'text' : 'password'}
                    value={passwordFormik.values.current_password}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.current_password && Boolean(passwordFormik.errors.current_password)}
                    helperText={passwordFormik.touched.current_password && passwordFormik.errors.current_password}
              margin="normal"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => togglePasswordVisibility('current')}
                          edge="end"
                        >
                          {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
            />
            <TextField
              fullWidth
              id="new_password"
              name="new_password"
              label="Neues Passwort"
                    type={showPassword.new ? 'text' : 'password'}
                    value={passwordFormik.values.new_password}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.new_password && Boolean(passwordFormik.errors.new_password)}
                    helperText={passwordFormik.touched.new_password && passwordFormik.errors.new_password}
              margin="normal"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => togglePasswordVisibility('new')}
                          edge="end"
                        >
                          {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
            />
            <TextField
              fullWidth
              id="confirm_password"
              name="confirm_password"
              label="Neues Passwort wiederholen"
                    type={showPassword.confirm ? 'text' : 'password'}
                    value={passwordFormik.values.confirm_password}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.confirm_password && Boolean(passwordFormik.errors.confirm_password)}
                    helperText={passwordFormik.touched.confirm_password && passwordFormik.errors.confirm_password}
              margin="normal"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => togglePasswordVisibility('confirm')}
                          edge="end"
                        >
                          {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Passwort ändern'}
            </Button>
          </form>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" gutterBottom>Zwei-Faktor-Authentifizierung</Typography>
            {user?.mfa_enabled ? (
                  <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={true}
                      onChange={() => setShowMFADisable(true)}
                      disabled={loading}
                    />
                  }
                      label="Zwei-Faktor-Authentifizierung aktiviert"
                />
                    <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                        onClick={handleShowBackupCodes}
                        disabled={loading}
                        sx={{ mr: 2 }}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Backup-Codes anzeigen'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowMFADisable(true)}
                      >
                        Deaktivieren
                  </Button>
                </Box>
                  </Box>
            ) : (
              <Box>
                <Typography variant="body1" gutterBottom>
                      Aktiviere die Zwei-Faktor-Authentifizierung, um dein Konto besser zu schützen.
                    </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleMFAEnable}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Zwei-Faktor-Authentifizierung einrichten'}
                </Button>
              </Box>
            )}
          </Box>
        )}


            {/* Devices & Sessions Tab */}
            {activeTab === 2 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Aktive Sitzungen</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleTerminateSuspiciousSessions}
                      disabled={loading || !sessionSecurity || sessionSecurity.suspicious_sessions === 0}
                      sx={{
                        opacity: (!sessionSecurity || sessionSecurity.suspicious_sessions === 0) ? 0.5 : 1
                      }}
                    >
                      Verdächtige Sitzungen beenden ({sessionSecurity?.suspicious_sessions || 0})
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setShowLogoutAllDialog(true)}
                      disabled={loading || otherSessionsCount === 0}
                      sx={{
                        opacity: otherSessionsCount === 0 ? 0.5 : 1
                      }}
                    >
                      Alle anderen Sitzungen beenden ({otherSessionsCount})
                    </Button>
                  </Box>
                </Box>

                {/* Session Security Status */}
                {sessionSecurity && (
                  <Box sx={{ mb: 3 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Sitzungssicherheit
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SecurityIcon color="primary" />
                              <Typography variant="body2">
                                Aktive Sitzungen: {sessionSecurity.total_active_sessions}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WarningIcon color={sessionSecurity.suspicious_sessions > 0 ? "warning" : "success"} />
                              <Typography variant="body2">
                                Verdächtige Sitzungen: {sessionSecurity.suspicious_sessions}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon color="success" />
                              <Typography variant="body2">
                                Sitzungsbindung: Aktiviert
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon color="success" />
                              <Typography variant="body2">
                                Anomalie-Erkennung: Aktiviert
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        
                        {/* Current Session Anomalies */}
                        {sessionSecurity.current_session_anomalies && 
                         sessionSecurity.current_session_anomalies.anomalies.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                              Aktuelle Sitzung - Anomalien erkannt:
                            </Typography>
                            <ul>
                              {sessionSecurity.current_session_anomalies.anomalies.map((anomaly, index) => (
                                <li key={index}>
                                  <Typography variant="body2">{anomaly}</Typography>
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                )}

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : activeSessions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Keine aktiven Sitzungen gefunden.
                  </Typography>
                ) : (
                  <>
                    <List>
                      {activeSessions.map((session) => {
                        const DeviceIcon = getDeviceIcon(session.device?.icon);
                        return (
                          <ListItem key={session.id} divider>
                            <ListItemIcon>
                              <DeviceIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" fontWeight="medium">
                                    {session.device?.browser || 'Unbekannter Browser'} • {session.device?.os || 'Unbekanntes OS'}
                                  </Typography>
                                  {session.current && (
                                    <Chip label="Dieses Gerät" color="primary" size="small" />
                                  )}
                                  {session.suspicious?.is_suspicious && (
                                    <Chip 
                                      label="Verdächtig" 
                                      color="warning" 
                                      size="small"
                                      icon={<WarningIcon />}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {session.location} • {session.ip}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Letzte Aktivität: {session.lastActivity ? new Date(session.lastActivity).toLocaleString('de-DE') : 'Unbekannt'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Läuft ab: {session.expires_at ? new Date(session.expires_at).toLocaleString('de-DE') : 'Unbekannt'}
                                  </Typography>
                                  {session.suspicious?.is_suspicious && (
                                    <Box sx={{ mt: 1 }}>
                                      <Alert severity="warning" sx={{ py: 0.5 }}>
                                        <Typography variant="body2" fontWeight="bold">
                                          Verdächtige Aktivität erkannt:
                                        </Typography>
                                        <Typography variant="body2">
                                          {session.suspicious.reason}
                                        </Typography>
                                        {session.suspicious.last_ip_change && (
                                          <Typography variant="body2">
                                            Letzte IP-Änderung: {new Date(session.suspicious.last_ip_change).toLocaleString('de-DE')}
                                          </Typography>
                                        )}
                                        {session.suspicious.last_ua_change && (
                                          <Typography variant="body2">
                                            Letzte Browser-Änderung: {new Date(session.suspicious.last_ua_change).toLocaleString('de-DE')}
                                          </Typography>
                                        )}
                                      </Alert>
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>

                    {/* Sessions Pagination */}
                    <Box mt={2} textAlign="center">
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Zeige {activeSessions.length} von {sessionsTotal} Sitzungen
                      </Typography>
                      {sessionsHasMore && (
                        <Button
                          variant="outlined"
                          onClick={loadMoreSessions}
                          startIcon={<DevicesIcon />}
                        >
                          Weitere Sitzungen laden
                        </Button>
                      )}
                    </Box>
                  </>
                )}

              </Box>
            )}

            {/* Login History Tab */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon />
                  Anmeldeverlauf
                </Typography>

                {/* Summary Statistics (styled like Sitzungssicherheit) */}
                {loginHistorySummary && (
                  <Box sx={{ mb: 3 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Schnellübersicht
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ComputerIcon color="primary" />
                              <Typography variant="body2">
                                Desktop: {loginHistorySummary.desktop_logins || 0}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneAndroidIcon color="primary" />
                              <Typography variant="body2">
                                Mobile: {loginHistorySummary.mobile_logins || 0}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PlaceIcon color="info" />
                              <Typography variant="body2">
                                Orte: {loginHistorySummary.unique_locations || 0}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTimeIcon color="action" />
                              <Typography variant="body2">
                                Heute: {loginHistorySummary.today_logins || 0}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                )}

                {/* Search and Filter Controls */}
                <Box sx={{ mb: 3 }}>
                  {/* Search Input */}
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Suche nach Browser, IP-Adresse, Ort..."
                    value={loginHistorySearch}
                    onChange={(e) => handleLoginHistorySearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  {/* Filter Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, sm: 1 }, 
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <Button
                      variant={loginHistoryFilter === 'all' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleLoginHistoryFilter('all')}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minWidth: { xs: 'auto', sm: 'auto' },
                        px: { xs: 1, sm: 2 }
                      }}
                    >
                      Alle
                    </Button>
                    <Button
                      variant={loginHistoryFilter === 'recent' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleLoginHistoryFilter('recent')}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minWidth: { xs: 'auto', sm: 'auto' },
                        px: { xs: 1, sm: 2 }
                      }}
                    >
                      Letzte 7 Tage
                    </Button>
                    <Button
                      variant={loginHistoryFilter === 'desktop' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleLoginHistoryFilter('desktop')}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minWidth: { xs: 'auto', sm: 'auto' },
                        px: { xs: 1, sm: 2 }
                      }}
                    >
                      💻 Desktop
                    </Button>
                    <Button
                      variant={loginHistoryFilter === 'mobile' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleLoginHistoryFilter('mobile')}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minWidth: { xs: 'auto', sm: 'auto' },
                        px: { xs: 1, sm: 2 }
                      }}
                    >
                      📱 Mobile
                    </Button>
                  </Box>
                </Box>

                {/* Login History List (collapsible as a whole) */}
                {loading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : loginHistory.length === 0 ? (
                  <Alert severity="info">
                    {loginHistorySearch || loginHistoryFilter !== 'recent' 
                      ? 'Keine Ergebnisse gefunden.' 
                      : 'Kein Anmeldeverlauf verfügbar.'}
                  </Alert>
                ) : (
                  <Box>
                    <Accordion sx={{ border: 1, borderColor: 'divider' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Einträge ({loginHistoryTotal})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {loginHistory.map((login, index) => (
                            <ListItem key={login.id || index} sx={{ 
                              border: 1, 
                              borderColor: 'divider', 
                              borderRadius: 1, 
                              mb: 1,
                              px: { xs: 1, sm: 2 },
                              py: { xs: 1, sm: 1.5 }
                            }}>
                              <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                                {login.device_type === 'mobile' ? <PhoneAndroidIcon /> : <ComputerIcon />}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography 
                                      variant="subtitle1"
                                      sx={{ 
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        lineHeight: { xs: 1.2, sm: 1.4 }
                                      }}
                                    >
                                      {login.device_type === 'mobile' ? '📱' : '💻'} {login.browser} auf {login.operating_system}
                                    </Typography>
                                    <Chip 
                                      label={login.success ? 'Erfolgreich' : 'Fehlgeschlagen'} 
                                      color={login.success ? 'success' : 'error'} 
                                      size="small"
                                      sx={{ 
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        height: { xs: 20, sm: 24 }
                                      }}
                                    />
                                    {login.session?.is_suspicious && (
                                      <Chip 
                                        label="Verdächtig" 
                                        color="warning" 
                                        size="small"
                                        icon={<WarningIcon />}
                                        sx={{ 
                                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                          height: { xs: 20, sm: 24 }
                                        }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{ 
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        lineHeight: { xs: 1.3, sm: 1.4 }
                                      }}
                                    >
                                      📍 {login.location || 'Unbekannte Position'}
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{ 
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        lineHeight: { xs: 1.3, sm: 1.4 }
                                      }}
                                    >
                                      🕒 {login.timestamp instanceof Date ? login.timestamp.toLocaleString('de-DE') : new Date(login.timestamp).toLocaleString('de-DE')}
                                    </Typography>
                                    {login.ip_address && (
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ 
                                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                          lineHeight: { xs: 1.3, sm: 1.4 }
                                        }}
                                      >
                                        🌐 IP: {login.ip_address}
                                      </Typography>
                                    )}
                                    {login.session?.is_suspicious && (
                                      <Box sx={{ mt: 1 }}>
                                        <Alert 
                                          severity="warning" 
                                          sx={{ 
                                            py: 0.5,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                          }}
                                        >
                                          <Typography 
                                            variant="body2" 
                                            fontWeight="bold"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                          >
                                            🚨 Verdächtige Aktivität erkannt:
                                          </Typography>
                                          <Typography 
                                            variant="body2"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                          >
                                            {login.session.anomaly_reason}
                                          </Typography>
                                        </Alert>
                                      </Box>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>

                        {/* Pagination Info and Load More Button inside accordion */}
                        <Box mt={2} textAlign="center">
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 1,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            Zeige {loginHistory.length} von {loginHistoryTotal} Einträgen
                          </Typography>
                          {loginHistory.length >= 5 && loginHistory.length < loginHistoryTotal && (
                            <Button
                              variant="outlined"
                              onClick={loadMoreLoginHistory}
                              startIcon={<HistoryIcon />}
                              size="small"
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                px: { xs: 2, sm: 3 },
                                py: { xs: 0.5, sm: 1 }
                              }}
                            >
                              Weitere laden
                            </Button>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            )}

            {/* Question History Tab */}
            {activeTab === 4 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuestionAnswerIcon />
                  Fragenverlauf
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Hier findest du alle deine gestellten Fragen und deren Antworten.
                </Typography>

                {questionsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : userQuestions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <QuestionAnswerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Noch keine Fragen gestellt
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Du hast noch keine Fragen zur Cybersicherheit gestellt.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {userQuestions.map((question, index) => {
                      const isExpanded = expandedQuestions.has(question.id);
                      const getStatusColor = (status) => {
                        switch (status) {
                          case 'answered': return 'success';
                          case 'pending': return 'warning';
                          case 'dismissed': return 'error';
                          default: return 'default';
                        }
                      };
                      const getStatusIcon = (status) => {
                        switch (status) {
                          case 'answered': return <CheckCircleIcon />;
                          case 'pending': return <PendingIcon />;
                          case 'dismissed': return <CancelIcon />;
                          default: return null;
                        }
                      };
                      const getStatusLabel = (status) => {
                        switch (status) {
                          case 'answered': return 'Beantwortet';
                          case 'pending': return 'Ausstehend';
                          case 'dismissed': return 'Abgelehnt';
                          default: return status;
                        }
                      };

                      return (
                        <Card key={question.id} variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                  <Chip
                                    icon={getStatusIcon(question.status)}
                                    label={getStatusLabel(question.status)}
                                    color={getStatusColor(question.status)}
                                    size="small"
                                  />
                                  {question.kategorie && (
                                    <Chip
                                      label={question.kategorie}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  {question.question}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Gestellt: {formatQuestionDate(question.created_at)}
                                  {question.answered_at && (
                                    <> • Beantwortet: {formatQuestionDate(question.answered_at)}</>
                                  )}
                                </Typography>
                              </Box>
                              {question.status === 'answered' && question.answer && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleQuestion(question.id)}
                                  sx={{ ml: 1 }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                            </Box>

                            {question.status === 'answered' && question.answer && (
                              <Collapse in={isExpanded}>
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Antwort:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {question.answer}
                                  </Typography>
                                </Box>
                              </Collapse>
                            )}

                            {question.status === 'dismissed' && question.dismiss_reason && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'error.main' }}>
                                  Grund für Ablehnung:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {question.dismiss_reason}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}
      </Paper>
        </Grid>

        {/* Right Column - Quick Actions */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Quick Actions Card */}
            <Card elevation={3}>
              <CardHeader title="Schnellaktionen" />
              <CardContent>
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportData}
                    disabled={loading}
                  >
                    Daten exportieren
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={logout}
                  >
                    Abmelden
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Account Status Card */}
            <Card elevation={3}>
              <CardHeader title="Kontostatus" />
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">E-Mail verifiziert</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {user?.mfa_enabled ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <WarningIcon color="warning" />
                    )}
                    <Typography variant="body2">
                      {user?.mfa_enabled ? '2FA aktiviert' : '2FA nicht aktiviert'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="info" />
                    <Typography variant="body2">
                      Konto erstellt: {formatDate(user?.created_at)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Danger Zone Card */}
            <Card elevation={3} sx={{ border: '1px solid', borderColor: 'error.main' }}>
              <CardHeader 
                title="Gefahrenbereich" 
                titleTypographyProps={{ color: 'error.main' }}
              />
              <CardContent>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setShowDeleteDialog(true)}
                >
                  Konto löschen
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* MFA Setup Dialog */}
      {showMFASetup && (
        <MFASetup
          onComplete={handleMFASetupComplete}
          onCancel={handleMFASetupCancel}
        />
      )}

      {/* MFA Disable Dialog */}
      <Dialog open={showMFADisable} onClose={() => setShowMFADisable(false)}>
        <DialogTitle>Zwei-Faktor-Authentifizierung deaktivieren</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Bitte gib den Verifizierungscode aus deiner Authentifikator-App oder einen Backup-Code ein, um die Zwei-Faktor-Authentifizierung zu deaktivieren.
          </Typography>
          <TextField
            fullWidth
            label="Verifizierungscode"
            value={mfaDisableCode}
            onChange={(e) => setMfaDisableCode(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMFADisable(false)}>Abbrechen</Button>
          <Button 
            onClick={handleMFADisable} 
            variant="contained" 
            color="primary"
            disabled={loading || !mfaDisableCode}
          >
            {loading ? <CircularProgress size={24} /> : 'Zwei-Faktor-Authentifizierung deaktivieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onClose={handleCloseBackupCodes} maxWidth="sm" fullWidth>
        <DialogTitle>Backup-Codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bitte speichere diese Backup-Codes an einem sicheren Ort. Du kannst sie verwenden, um auf dein Konto zuzugreifen, falls du den Zugriff auf deine Authentifikator-App verlierst.
          </Alert>
          <List>
            {backupCodes.map((code, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText 
                    primary={code}
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontFamily: 'monospace',
                        fontSize: '1.1rem',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                </ListItem>
                {index < backupCodes.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBackupCodes}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle color="error">Konto löschen</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Diese Aktion kann nicht rückgängig gemacht werden. Alle deine Daten werden permanent gelöscht.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Um dein Konto zu löschen, gib bitte "LÖSCHEN" in das Feld unten ein:
          </Typography>
          <TextField
            fullWidth
            label="Bestätigung eingeben"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Abbrechen</Button>
          <Button 
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={loading || deleteConfirmText !== 'LÖSCHEN'}
          >
            {loading ? <CircularProgress size={24} /> : 'Konto löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout All Sessions Dialog */}
      <Dialog open={showLogoutAllDialog} onClose={() => setShowLogoutAllDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle color="warning">Alle anderen Sitzungen beenden</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Diese Aktion wird {otherSessionsCount} andere aktive Sitzung{otherSessionsCount !== 1 ? 'en' : ''} beenden. Du bleibst in der aktuellen Sitzung angemeldet.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Möchtest du wirklich alle anderen Sitzungen beenden? Dies kann andere Geräte oder Browser-Tabs abmelden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogoutAllDialog(false)}>Abbrechen</Button>
          <Button 
            onClick={handleLogoutAllSessions}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Alle anderen Sitzungen beenden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile; 